//Global imports
import { useState } from 'react';
import { cilImage, cilPencil, cilPlus } from '@coreui/icons';
import CIcon from '@coreui/icons-react';
import {
    CButton,
    CCard,
    CCardBody,
    CCardFooter,
    CCardHeader,
    CCardText,
    CCardTitle,
    CCol,
} from '@coreui/react';
import { useNavigate } from 'react-router-dom';

//File imports
import { Language } from '../../../../language/English';
import { ArticlePojo } from '../../Api/ArticlePojo';
import {
    ARTICLE_STATUS_ACTIVE,
    ARTICLE_STATUS_INACTIVE,
} from '../../Api/ArticleConstants';
import { ARTICLE_TRANSLATION } from '../../../../PagesNames';

//Component imports
import ConfirmationBoxModal from '../../../../components/Layout/ConfirmationBoxModal';

const ArticleCard = (props: {
    article: ArticlePojo;
    key: number;
    deleteArticle: () => void;
    changeArticleStatus: (status: number) => void;
    onEdit: (article: ArticlePojo) => void;
}) => {
    const data = props.article;
    const [deleteArticleConfirmation, setDeleteArticleConfirmation] =
        useState<boolean>(false);
    const navigate = useNavigate();

    const closeDeleteArticleConfirmation = () =>
        setDeleteArticleConfirmation(false);

    const openDeleteArticleConfirmation = () =>
        setDeleteArticleConfirmation(true);

    const _deleteArticle = () => {
        closeDeleteArticleConfirmation();
        props.deleteArticle();
    };

    const changeArticleStatus = () => {
        closeDeleteArticleConfirmation();
        if (data.status === 1) {
            props.changeArticleStatus(ARTICLE_STATUS_INACTIVE);
        } else {
            props.changeArticleStatus(ARTICLE_STATUS_ACTIVE);
        }
    };

    const handleLanguageClick = (language: string, status: number) => {
        navigate(ARTICLE_TRANSLATION, {
            state: {
                language,
                id: data.articleId,
                isEdit: status,
                name: data.name,
                isFooterLink: data.isFooterLink,
            },
        });
    };

    return (
        <CCol xs={12} md={4} className="mb-2">
            <CCard className="border-0 shadow-sm rounded-2">
                <CCardHeader>
                    <div className="d-flex justify-content-between align-items-center">
                        <div
                            className="close-button"
                            onClick={openDeleteArticleConfirmation}
                        >
                            <span>X</span>
                        </div>
                        <div className={`label-position`}>
                            <div
                                className={`${data.status === 1 ? 'article-label' : 'article-label disable'}`}
                            >
                                {data.status === 1
                                    ? Language.ACTIVE
                                    : Language.INACTIVE}
                            </div>
                        </div>
                    </div>
                </CCardHeader>
                <CCardBody className="p-0">
                    <div>
                        {data.thumbnail ? (
                            <img
                                src={data.thumbnail}
                                alt={data.name}
                                className="w-100 mb-1"
                                style={{ height: '180px', objectFit: 'cover' }}
                            />
                        ) : (
                            <div
                                className="w-100 bg-light mb-1 d-flex justify-content-center align-items-center"
                                style={{ height: '180px' }}
                            >
                                <CIcon
                                    icon={cilImage}
                                    size="xxl"
                                    className="text-secondary"
                                />
                            </div>
                        )}
                    </div>
                    <div className="px-2">
                        <div className="d-flex justify-content-between align-items-start">
                            <CCardTitle
                                className="fs-3 text-truncate"
                                style={{ maxWidth: '100%' }}
                            >
                                {data.name}
                            </CCardTitle>
                        </div>
                        <CCardText className="mb-2">
                            <span className="fw-bold">
                                {Language.IS_FOOTER_LINK} :
                            </span>{' '}
                            {data.isFooterLink ? Language.YES : Language.NO}
                        </CCardText>

                        <div className="d-flex gap-2 language-button mb-1">
                            <CButton
                                color="primary"
                                onClick={() =>
                                    handleLanguageClick(
                                        'en',
                                        data.translations.en,
                                    )
                                }
                            >
                                <CIcon
                                    icon={
                                        data.translations.en === 0
                                            ? cilPlus
                                            : cilPencil
                                    }
                                    className="me-2"
                                />
                                {Language.ENGLISH}
                            </CButton>
                            <CButton
                                color="info"
                                onClick={() =>
                                    handleLanguageClick(
                                        'fr',
                                        data.translations.fr,
                                    )
                                }
                            >
                                <CIcon
                                    icon={
                                        data.translations.fr === 0
                                            ? cilPlus
                                            : cilPencil
                                    }
                                    className="me-2"
                                />
                                {Language.FRENCH}
                            </CButton>
                            <CButton
                                color="warning"
                                onClick={() =>
                                    handleLanguageClick(
                                        'de',
                                        data.translations.de,
                                    )
                                }
                            >
                                <CIcon
                                    icon={
                                        data.translations.de === 0
                                            ? cilPlus
                                            : cilPencil
                                    }
                                    className="me-2"
                                />
                                {Language.GERMAN}
                            </CButton>
                        </div>
                    </div>
                </CCardBody>
                <CCardFooter className="bg-white px-2">
                    <div className="d-flex gap-2">
                        <CButton
                            color="primary"
                            size="sm"
                            onClick={() => props.onEdit(data)}
                        >
                            <CIcon icon={cilPencil} className="me-2" />
                            {Language.EDIT_ARTICLE}
                        </CButton>
                        <CButton
                            onClick={changeArticleStatus}
                            color={`${data.status === 1 ? 'secondary' : 'success'}`}
                            className="me-2"
                        >
                            {`${data.status === 1 ? Language.INACTIVATE : Language.ACTIVATE}`}
                        </CButton>
                    </div>
                </CCardFooter>
            </CCard>

            {deleteArticleConfirmation && (
                <ConfirmationBoxModal
                    onClose={closeDeleteArticleConfirmation}
                    title={Language.DELETE_ARTICLE}
                    message={`${Language.DELETE_CONFIRMATION_WITH_DOCUMENT_NAME} ${data.name} ?`}
                    buttonOneText={Language.YES}
                    buttonTwoText={Language.CLOSE}
                    buttonOneCallable={_deleteArticle}
                    buttonTwoCallable={closeDeleteArticleConfirmation}
                    buttonOneCallBackFunctionParams={1}
                    buttonTwoCallBackFunctionParams={null}
                />
            )}
        </CCol>
    );
};

export default ArticleCard;
